import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

export default function ResetPassword() {
  const [params] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const userId = params.get('uid') || '';
  const forced = params.get('forced') === 'true'; // Check if this is a forced reset after login

  // Check if user is logged in (for forced reset)
  useEffect(() => {
    if (forced) {
      // Verify user is logged in
      fetch('/auth/me', { credentials: 'include' })
        .then(res => {
          if (!res.ok) {
            // Not logged in, redirect to login
            navigate('/login');
          }
        })
        .catch(() => {
          navigate('/login');
        });
    }
  }, [forced, navigate]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setMsg(null);

    // Validation
    if (password.length < 8) {
      setMsg('Password must be at least 8 characters');
      return;
    }

    if (forced) {
      // Forced reset after login - use session-based change-password endpoint
      if (password !== confirmPassword) {
        setMsg('Passwords do not match');
        return;
      }

      if (!currentPassword) {
        setMsg('Please enter your current password');
        return;
      }

      try {
        const res = await fetch('/auth/change-password', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include', // Include session cookie
          body: JSON.stringify({ currentPassword, newPassword: password }),
        });

        if (res.ok) {
          setMsg('Password updated successfully. Redirecting...');
          setTimeout(() => navigate('/'), 1200);
        } else {
          const error = await res.json();
          setMsg(error.error || 'Failed to update password');
        }
      } catch (err: any) {
        setMsg(err.message || 'Failed to update password');
      }
    } else {
      // Email token reset - use existing endpoint
      if (!token || !userId) {
        setMsg('Invalid reset link');
        return;
      }

      const res = await fetch('/api/auth/reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, userId, password }),
      });

      if (res.ok) {
        setMsg('Password updated. Redirecting to login...');
        setTimeout(() => navigate('/login'), 1200);
      } else {
        setMsg('Reset failed. Link may be invalid or expired.');
      }
    }
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Dark Blue Background with Logo */}
      <div 
        className="hidden md:flex md:w-2/3 bg-[#0D227A] items-center justify-center"
      >
        <img 
          src="/images/bb_primary logo (outlined).svg" 
          alt="Bright Blue Logo" 
          className="h-32 w-auto"
        />
      </div>

      {/* Right Side - White Background with Form */}
      <div className="w-full md:w-1/3 bg-white flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Reset Password Card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-8">
            <h1 className="text-2xl font-bold mb-6 text-black text-center">
              {forced ? 'Change Password' : 'Reset password'}
            </h1>
            {forced && (
              <p className="text-sm text-gray-600 mb-6 text-center">
                You must change your password before continuing.
              </p>
            )}
            <form className="space-y-5" onSubmit={submit}>
              {forced && (
                <div className="space-y-2">
                  <Label htmlFor="currentPassword" className="text-sm font-medium text-gray-700">
                    Current password
                  </Label>
                  <Input 
                    id="currentPassword" 
                    type="password" 
                    value={currentPassword} 
                    onChange={e => setCurrentPassword(e.target.value)} 
                    required 
                    className="w-full border-gray-300"
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                  New password
                </Label>
                <Input 
                  id="password" 
                  type="password" 
                  value={password} 
                  onChange={e => setPassword(e.target.value)} 
                  required 
                  minLength={8}
                  className="w-full border-gray-300"
                />
                <p className="text-xs text-gray-500">Must be at least 8 characters</p>
              </div>
              {forced && (
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">
                    Confirm new password
                  </Label>
                  <Input 
                    id="confirmPassword" 
                    type="password" 
                    value={confirmPassword} 
                    onChange={e => setConfirmPassword(e.target.value)} 
                    required 
                    className="w-full border-gray-300"
                  />
                </div>
              )}
              <Button 
                type="submit" 
                className="w-full bg-[#0047FF] hover:bg-[#0039CC] text-white py-6 text-base font-semibold"
              >
                {forced ? 'Change Password' : 'Update password'}
              </Button>
            </form>
            {msg && (
              <p className={`text-sm mt-4 text-center ${msg.includes('successfully') || msg.includes('updated') ? 'text-green-600' : 'text-red-600'}`}>
                {msg}
              </p>
            )}
          </div>

          {/* Powered by Section */}
          <div className="flex items-center justify-center gap-3">
            <p className="text-sm text-gray-600">Powered by</p>
            <img 
              src="/images/bb_primary logo (dark).svg" 
              alt="Bright Blue Logo" 
              className="h-8 w-auto"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
